import { axios } from '@/plugins/axios';
import { Statement, Pagination, Resource } from '@/helpers/Interfaces';
import { AccountType } from '@/helpers/Enums';
import { Position } from '@/helpers/Position';

/**
 * OrganizationService
 */
export default class OrganizationService
{
    /**
     * @param expanded number[]
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(expanded: number[]): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>('admin/organization', {
            params: {
                expanded: expanded.join(',')
            }
        })).data;
    }

    /**
     * @returns Promise<ListItemModel[]>
     */
    public static async getOptions(): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>('admin/organization/options')).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(id: number): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/organization/${id}`)).data;
    }

    /**
     * @param model FormModel
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async create(model: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.post<Resource<FormModel>>(`admin/organization`, model)).data;
    }

    /**
     * @param id number
     * @param model FormModel
     *
     * @returns Promise<Statement>
     */
    public static async update(id: number, model: FormModel): Promise<Statement>
    {
        return (await axios.put<Statement>(`admin/organization/${id}`, model)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Statement>
     */
    public static async remove(id: number): Promise<Statement>
    {
        return (await axios.delete(`admin/organization/${id}`)).data as Statement;
    }

    /**
     * @param positions PagePosition[]
     *
     * @returns Promise<Statement>
     */
    public static async sortNodes(positions: Position[]): Promise<Statement>
    {
        return (await axios.put<Statement>(`admin/organization/positions`, positions)).data;
    }
}

export interface ListItemModel
{
    id: number;
    parentId?: number;
    name: string;
    value: number;
    hasChildren: boolean;
    position: number;
    isActive: boolean;
    accountTypeEnum: number;
    text: string;
}

export interface FormModel
{
    id: number;
    parentId?: number;
    name: string;
    isActive: boolean;
    accountType: AccountType;
    roles: number[];
    type: string;
    payload: UnitPayload;
}

export interface UnitPayload
{
    isAdmin: boolean;
    isOwner: boolean;
    isRepresentative: boolean;
    isUser: boolean;
    isSeller: boolean;
    isBusinessJoiner: boolean;
    isJoiner: boolean;
}
